export type PeriodKey = 'denne' | 'n3' | 'n6' | 'aar' | 'naar' | 'velg';
export type MetricKey = 'oms' | 'bf';

export interface ForecastMonth {
  monthKey: string; // 'YYYY-MM'
  label: string;    // 'jul. 26'
  longLabel: string; // 'Juli 2026'
  omsFakt: number;
  omsRest: number;
  bfFakt: number;
  bfRest: number;
}

/**
 * VIB-2870: ett prosjekts bidrag til én måned. Fakturert kommer fra fakturaer
 * datert i måneden; rest er estimert gjenstående plassert i måneden (på
 * estimated_deadline, eller klemt til inneværende måned når fristen er
 * passert). Brukes av drilldownen bak søylene i Prognose-widgeten.
 */
export interface ForecastContribution {
  projectId: string;
  projectCode: string | null;
  projectName: string;
  productionLeadName: string | null;
  omsFakt: number;
  omsRest: number;
  bfFakt: number;
  bfRest: number;
  /** projects.total_amount — grunnlaget resten regnes fra. */
  contractTotal: number;
  /** Alt fakturert på prosjektet uansett måned, så resten kan kontrolleres. */
  invoicedToDate: number;
  estimatedDeadline: string | null;
  /** Fristen er passert, så resten ble flyttet til inneværende måned. */
  overdueClamped: boolean;
}

export interface ForecastInvoiceInput {
  invoice_date: string | null;
  amount_excl_vat: number | null;
  project_id: string | null;
}

export interface ForecastProjectInput {
  id: string;
  name: string | null;
  project_code: string | null;
  production_lead_id: string | null;
  total_amount: number | null;
  bf_estimated: number | null;
  estimated_deadline: string | null;
  project_status: string;
}

export interface ForecastResult {
  months: ForecastMonth[];
  /** monthKey → bidrag per prosjekt, sortert på størst bidrag først. */
  contributionsByMonth: Record<string, ForecastContribution[]>;
}

export interface ForecastSummary {
  omsFakt: number;
  omsRest: number;
  omsTot: number;
  bfFakt: number;
  bfRest: number;
  bfTot: number;
}

export interface YAxisTick {
  value: number;
  topPct: number; // 0 = top, 1 = bottom
}

const MONTH_SHORT = ['jan.', 'feb.', 'mar.', 'apr.', 'mai.', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'];
const MONTH_LONG = ['Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'];

export function toMonthKey(year: number, month0: number): string {
  return `${year}-${String(month0 + 1).padStart(2, '0')}`;
}

/** Build empty ForecastMonth[] covering startYear-01 through endYear-12 */
export function buildMonthBuckets(startYear: number, endYear: number): ForecastMonth[] {
  const months: ForecastMonth[] = [];
  for (let y = startYear; y <= endYear; y++) {
    for (let m = 0; m < 12; m++) {
      const yy = String(y).slice(2);
      months.push({
        monthKey: toMonthKey(y, m),
        label: `${MONTH_SHORT[m]} ${yy}`,
        longLabel: `${MONTH_LONG[m]} ${y}`,
        omsFakt: 0,
        omsRest: 0,
        bfFakt: 0,
        bfRest: 0,
      });
    }
  }
  return months;
}

function diffMonths(from: string, to: string): number {
  return (Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * 12
    + (Number(to.slice(5, 7)) - Number(from.slice(5, 7)));
}

export function slicePeriod(
  allMonths: ForecastMonth[],
  period: PeriodKey,
  curKey: string,
  customFrom?: string,
  customTo?: string,
): ForecastMonth[] {
  const curYear = Number(curKey.slice(0, 4));
  switch (period) {
    case 'denne': return allMonths.filter((m) => m.monthKey === curKey);
    case 'n3': return allMonths.filter((m) => { const d = diffMonths(curKey, m.monthKey); return d >= 0 && d < 3; });
    case 'n6': return allMonths.filter((m) => { const d = diffMonths(curKey, m.monthKey); return d >= 0 && d < 6; });
    case 'aar': return allMonths.filter((m) => m.monthKey.startsWith(`${curYear}-`));
    case 'naar': return allMonths.filter((m) => m.monthKey.startsWith(`${curYear + 1}-`));
    case 'velg':
      if (customFrom && customTo) return allMonths.filter((m) => m.monthKey >= customFrom && m.monthKey <= customTo);
      return slicePeriod(allMonths, 'n6', curKey);
    default: return allMonths;
  }
}

export function computeYAxis(
  months: ForecastMonth[],
  metric: MetricKey,
  steps = 4,
): { ticks: YAxisTick[]; maxVal: number } {
  const maxStack = Math.max(1, ...months.map((m) =>
    metric === 'oms' ? m.omsFakt + m.omsRest : m.bfFakt + m.bfRest,
  ));
  const maxVal = Math.ceil(maxStack / 300_000) * 300_000 || 300_000;
  const ticks: YAxisTick[] = [];
  for (let s = 0; s <= steps; s++) {
    ticks.push({ value: (maxVal * s) / steps, topPct: 1 - s / steps });
  }
  return { ticks, maxVal };
}

export function computeSummary(months: ForecastMonth[]): ForecastSummary {
  const sum = (f: (m: ForecastMonth) => number) => months.reduce((a, m) => a + f(m), 0);
  return {
    omsFakt: sum((m) => m.omsFakt),
    omsRest: sum((m) => m.omsRest),
    omsTot: sum((m) => m.omsFakt + m.omsRest),
    bfFakt: sum((m) => m.bfFakt),
    bfRest: sum((m) => m.bfRest),
    bfTot: sum((m) => m.bfFakt + m.bfRest),
  };
}

export function getPeriodName(period: PeriodKey, curYear: number): string {
  const NAMES: Record<PeriodKey, string> = {
    denne: 'Denne måneden',
    n3: 'Neste 3 mnd',
    n6: 'Neste 6 mnd',
    aar: `Hele ${curYear}`,
    naar: `${curYear + 1}`,
    velg: 'Egendefinert periode',
  };
  return NAMES[period];
}

/**
 * Pure aggregation behind useInvoiceForecast. Fills the month buckets AND
 * keeps the per-project rows that make up each bucket (VIB-2870), so the
 * widget can show "hva ligger bak tallet" without a second query.
 *
 * Rules (see docs/kpi-source-of-truth.md «Prognose / Estimert sluttfaktura
 * per måned»):
 * - Fakturert lands in the invoice_date month; GEEK-andel = bf_estimated / total_amount.
 * - Estimert rest = total_amount − alt fakturert, only for active projects with a
 *   deadline and positive remaining, placed in the deadline month. A past
 *   deadline is clamped to the current month and flagged `overdueClamped`.
 */
export function computeForecastMonths(
  invoices: ForecastInvoiceInput[],
  projects: ForecastProjectInput[],
  curKey: string,
  buckets: ForecastMonth[],
  leadNames: Map<string, string> = new Map(),
): ForecastResult {
  const monthMap = new Map<string, ForecastMonth>();
  for (const m of buckets) monthMap.set(m.monthKey, m);

  const projectMap = new Map<string, ForecastProjectInput>();
  const bfMarginMap = new Map<string, number>();
  for (const p of projects) {
    projectMap.set(p.id, p);
    const total = Number(p.total_amount ?? 0);
    const bf = Number(p.bf_estimated ?? 0);
    bfMarginMap.set(p.id, total > 0 ? bf / total : 0);
  }

  // Total invoiced per project (all time) — for remaining calculation and
  // for the drilldown's "fakturert hittil" context.
  const invoicedPerProject = new Map<string, number>();
  for (const inv of invoices) {
    if (!inv.project_id) continue;
    const oms = Number(inv.amount_excl_vat ?? 0);
    invoicedPerProject.set(inv.project_id, (invoicedPerProject.get(inv.project_id) ?? 0) + oms);
  }

  const byMonth = new Map<string, Map<string, ForecastContribution>>();
  const contribution = (monthKey: string, p: ForecastProjectInput): ForecastContribution => {
    let perProject = byMonth.get(monthKey);
    if (!perProject) {
      perProject = new Map();
      byMonth.set(monthKey, perProject);
    }
    let row = perProject.get(p.id);
    if (!row) {
      row = {
        projectId: p.id,
        projectCode: p.project_code ?? null,
        projectName: p.name ?? '',
        productionLeadName: p.production_lead_id ? leadNames.get(p.production_lead_id) ?? null : null,
        omsFakt: 0,
        omsRest: 0,
        bfFakt: 0,
        bfRest: 0,
        contractTotal: Number(p.total_amount ?? 0),
        invoicedToDate: invoicedPerProject.get(p.id) ?? 0,
        estimatedDeadline: p.estimated_deadline ?? null,
        overdueClamped: false,
      };
      perProject.set(p.id, row);
    }
    return row;
  };

  // Attribute invoice amounts to month buckets by invoice_date
  for (const inv of invoices) {
    if (!inv.invoice_date || !inv.project_id) continue;
    const mk = inv.invoice_date.slice(0, 7);
    const bucket = monthMap.get(mk);
    if (!bucket) continue;

    const oms = Number(inv.amount_excl_vat ?? 0);
    const margin = bfMarginMap.get(inv.project_id) ?? 0;
    bucket.omsFakt += oms;
    bucket.bfFakt += oms * margin;

    const p = projectMap.get(inv.project_id);
    if (p) {
      const row = contribution(mk, p);
      row.omsFakt += oms;
      row.bfFakt += oms * margin;
    }
  }

  // Place estimated remaining for active projects in estimated_deadline
  // month. Overdue projects (deadline in the past, not fully invoiced)
  // are clamped to the current month — otherwise their remaining amount
  // lands in a past bucket that every forward-looking period slice
  // filters out, silently hiding it from the forecast.
  for (const p of projects) {
    if (p.project_status !== 'active') continue;
    const total = Number(p.total_amount ?? 0);
    if (total <= 0 || !p.estimated_deadline) continue;

    const alreadyInvoiced = invoicedPerProject.get(p.id) ?? 0;
    const remaining = total - alreadyInvoiced;
    if (remaining <= 0) continue;

    const deadlineKey = p.estimated_deadline.slice(0, 7);
    const clamped = deadlineKey < curKey;
    const mk = clamped ? curKey : deadlineKey;
    const bucket = monthMap.get(mk);
    if (!bucket) continue;

    const margin = bfMarginMap.get(p.id) ?? 0;
    bucket.omsRest += remaining;
    bucket.bfRest += remaining * margin;

    const row = contribution(mk, p);
    row.omsRest += remaining;
    row.bfRest += remaining * margin;
    row.overdueClamped = clamped;
  }

  const contributionsByMonth: Record<string, ForecastContribution[]> = {};
  for (const [mk, perProject] of byMonth) {
    contributionsByMonth[mk] = [...perProject.values()].sort(
      (a, b) => b.omsFakt + b.omsRest - (a.omsFakt + a.omsRest),
    );
  }

  return { months: buckets, contributionsByMonth };
}
